import React, { useState, useEffect, useMemo } from 'react';
import containerAPI from '../../services/containerAPI';
import { handleApiError } from '../../utils/helpers';
import { validateContainerNumber } from '../../utils/ContainerNumberValidator';
import LoadingSpinner from '../common/LoadingSpinner';
import { decodeJWT } from '../../utils/jwt';

const BulkContainerUpdate = ({ onUpdate }) => {
  // Initial form data (using ContainerForm fields)
  const initialBulkData = {
    containerNumber: '',
    bookingNumber: '',
    shippingLine: '',
    size: '20',
    type: 'GP',
    isoType: '',
    shipperName: '',
    consigneeName: '',
    portOfLoading: '',
    portOfDischarge: '',
    vesselName: '',
    voyageNumber: '',
    eta: '',
    etd: '',
    freeDays: ''
  };

  // States
  const [bulkData, setBulkData] = useState(initialBulkData);
  const [selectedContainers, setSelectedContainers] = useState([]);
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [result, setResult] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [portOptions, setPortOptions] = useState([]);
  const [portErrors, setPortErrors] = useState({ portOfLoading: '', portOfDischarge: '' });
  const [checkingContainer, setCheckingContainer] = useState(false);

  // Helper to ensure date string is yyyy-MM-dd
  const toDateInputValue = (val) => {
    if (!val) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
    if (/^\d{4}-\d{2}-\d{2}T/.test(val)) return val.slice(0, 10);
    return '';
  };

  // Filter containers based on search
  const filteredContainers = useMemo(() => {
    let filtered = containers;
    if (searchTerm.trim()) {
      // Support comma-separated search for container/booking numbers
      const terms = searchTerm.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
      if (terms.length > 1) {
        filtered = containers.filter(container =>
          terms.some(term =>
            container.containerNumber.toLowerCase().includes(term) ||
            container.bookingNumber.toLowerCase().includes(term)
          )
        );
      } else {
        const lowerSearch = terms[0];
        filtered = containers.filter(container =>
          container.containerNumber.toLowerCase().includes(lowerSearch) ||
          container.bookingNumber.toLowerCase().includes(lowerSearch) ||
          (container.shippingLine && container.shippingLine.toLowerCase().includes(lowerSearch)) ||
          (container.portOfLoading && container.portOfLoading.toLowerCase().includes(lowerSearch)) ||
          (container.portOfDischarge && container.portOfDischarge.toLowerCase().includes(lowerSearch)) ||
          (container.vesselName && container.vesselName.toLowerCase().includes(lowerSearch))
        );
      }
    }
    // Sort by containerNumber (alphanumeric)
    return filtered.slice().sort((a, b) => {
      if (!a.containerNumber) return 1;
      if (!b.containerNumber) return -1;
      return a.containerNumber.localeCompare(b.containerNumber, undefined, { numeric: true, sensitivity: 'base' });
    });
  }, [containers, searchTerm]);

  // Fetch data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const token = sessionStorage.getItem('agentToken');
        const agent = decodeJWT(token);
        const companyId = agent?.companyId || 1;
        
        // Load containers
        const containersResponse = await containerAPI.getAll(companyId);
        setContainers(containersResponse.data);
        
        // Load ports
        const portsResponse = await containerAPI.getPorts();
        setPortOptions(Array.isArray(portsResponse.data) ? portsResponse.data : []);
        
      } catch (error) {
        console.error('Error loading data:', error);
        setError('Failed to load initial data');
      }
    };
    
    loadData();
  }, []);

  // Check container existence when manually entered
  useEffect(() => {
    if (bulkData.containerNumber && bulkData.containerNumber.length >= 7) {
      const timeoutId = setTimeout(() => {
        checkContainerExists(bulkData.containerNumber);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [bulkData.containerNumber]);

  // Handle form field changes
  const handleBulkDataChange = (e) => {
    const { name, value } = e.target;
    setBulkData(prev => ({
      ...prev,
      [name]: (name === 'etd' || name === 'eta') ? toDateInputValue(value) : value
    }));

    // Validate port fields
    if (name === 'portOfLoading' || name === 'portOfDischarge') {
      const validPort = portOptions.some(port => port.portCode === value);
      setPortErrors(prev => ({
        ...prev,
        [name]: validPort || value === '' ? '' : 'Invalid port code. Please select a port from the list.'
      }));
    }
  };

  // Check if a container exists and load its data
  const checkContainerExists = async (containerNumber) => {
    if (!containerNumber || containerNumber.length < 4) return;
    
    setCheckingContainer(true);
    try {
      const response = await containerAPI.getByNumber(containerNumber);
      const data = response.data?.container || response.data;
      
      if (data && data.containerNumber === containerNumber) {
        // Auto-populate form with existing container data
        setBulkData({
          containerNumber: data.containerNumber || '',
          bookingNumber: data.bookingNumber || '',
          shippingLine: data.shippingLine || '',
          size: data.size || '20',
          type: data.type || 'GP',
          isoType: data.isoType || '',
          shipperName: data.shipperName || '',
          consigneeName: data.consigneeName || '',
          portOfLoading: data.portOfLoading || '',
          portOfDischarge: data.portOfDischarge || '',
          vesselName: data.vesselName || '',
          voyageNumber: data.voyageNumber || '',
          eta: toDateInputValue(data.eta) || '',
          etd: toDateInputValue(data.etd) || '',
          freeDays: data.freeDays || ''
        });
      }
    } catch (error) {
      // Container doesn't exist, keep form as is
      console.log('Container not found, creating new one');
    } finally {
      setCheckingContainer(false);
    }
  };

  // Handle bulk update submission
  const handleBulkUpdate = async () => {
    // Validate selected containers
    if (selectedContainers.length === 0) {
      alert('Please select at least one container');
      return;
    }

    // Validate required fields
    if (!bulkData.bookingNumber || !bulkData.shippingLine) {
      alert('Please fill in all required fields (Container Number, Booking Number, Carrier Source)');
      return;
    }

    // Validate ports before submit
    const validLoading = portOptions.some(port => port.portCode === bulkData.portOfLoading);
    const validDischarge = portOptions.some(port => port.portCode === bulkData.portOfDischarge);
    if (!validLoading || !validDischarge) {
      setPortErrors({
        portOfLoading: validLoading ? '' : 'Invalid port code. Please select a port from the list.',
        portOfDischarge: validDischarge ? '' : 'Invalid port code. Please select a port from the list.'
      });
      alert('Please select valid ports from the list for both Port of Loading and Port of Discharge.');
      return;
    }

    setLoading(true);
    setResult(null);
    setError('');
    setMessage('');

    try {
      // Prepare payload for each container
      const payloads = selectedContainers.map(containerNumber => {
        const payload = { ...bulkData };
        payload.containerNumber = containerNumber; // Override with selected container
        
        // Ensure etd and eta are full ISO datetime strings
        if (payload.etd && /^\d{4}-\d{2}-\d{2}$/.test(payload.etd)) {
          payload.etd = payload.etd + 'T00:00:00';
        }
        if (payload.eta && /^\d{4}-\d{2}-\d{2}$/.test(payload.eta)) {
          payload.eta = payload.eta + 'T00:00:00';
        }

        return payload;
      });

      // Process each container update/create
      const results = [];
      const failures = [];

      for (const payload of payloads) {
        try {
          // Check if container exists
          try {
            await containerAPI.getByNumber(payload.containerNumber);
            // Container exists - update it
            await containerAPI.update(payload.containerNumber, payload);
            results.push({ containerNumber: payload.containerNumber, success: true, action: 'updated' });
          } catch(error) {
            const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
            failures.push({ containerNumber: payload.containerNumber, error: errorMsg });
          }
        } catch (error) {
          const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
          failures.push({ containerNumber: payload.containerNumber, error: errorMsg });
        }
      }

      // Process results
      const successCount = results.length;
      const failureCount = failures.length;

      setResult({
        successCount,
        failureCount,
        failedContainers: failures.map(f => f.containerNumber),
        failureReasons: failures.map(f => f.error),
        details: results
      });

      setMessage(`${successCount} containers processed successfully!`);

      // Refresh container list
      const token = sessionStorage.getItem('agentToken');
      const agent = decodeJWT(token);
      const companyId = agent?.companyId || 1;
      const refreshResponse = await containerAPI.getAll(companyId);
      setContainers(refreshResponse.data);

      // Notify parent component
      if (onUpdate) onUpdate();

      // Reset form (keep selected containers for review)
      setBulkData(initialBulkData);

    } catch (error) {
      const errorMsg = handleApiError(error);
      setError(`Bulk operation failed: ${errorMsg}`);
      setResult({
        successCount: 0,
        failureCount: selectedContainers.length,
        failedContainers: selectedContainers,
        failureReasons: [errorMsg]
      });
    } finally {
      setLoading(false);
    }
  };

  // Container selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedContainers(filteredContainers.map(c => c.containerNumber));
    } else {
      setSelectedContainers([]);
    }
  };

  const handleSelectContainer = (containerNumber, checked) => {
    if (checked) {
      setSelectedContainers(prev => [...prev, containerNumber]);
    } else {
      setSelectedContainers(prev => prev.filter(cn => cn !== containerNumber));
    }
  };

  // Add manually entered container to selection
  const handleAddManualContainer = () => {
    if (!bulkData.containerNumber) {
      setError('Please enter a container number');
      return;
    }

    if (!validateContainerNumber(bulkData.containerNumber)) {
      setError('Invalid container number format');
      return;
    }

    if (!selectedContainers.includes(bulkData.containerNumber)) {
      setSelectedContainers(prev => [...prev, bulkData.containerNumber]);
      setMessage(`Added ${bulkData.containerNumber} to selection`);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  return (
    <div className="bulk-container-update">
      <div className="tool-header">
        <h3>Bulk Container Management</h3>
        <p>Create or update multiple containers with the same details</p>
      </div>

      {/* Messages */}
      {message && (
        <div className="alert alert-success">
          {message}
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      <div className="bulk-layout">
        {/* Left Panel - Container Form Fields */}
        <div className="form-panel">
          <div className="form-card">
            <h4>Container Details</h4>
            
            {/* Container Information */}
            <div className="form-row">
              
              <div className="form-group">
                <label>Booking Number *</label>
                <input
                  type="text"
                  name="bookingNumber"
                  value={bulkData.bookingNumber}
                  onChange={handleBulkDataChange}
                  disabled={loading}
                  placeholder="e.g., SHIP-6482"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Carrier Source *</label>
                <input
                  type="text"
                  name="shippingLine"
                  value={bulkData.shippingLine}
                  onChange={handleBulkDataChange}
                  disabled={loading}
                  placeholder="e.g., CMA CGM"
                  required
                />
              </div>
            </div>

            {/* Port Information */}
            <div className="form-row">
              <div className="form-group">
                <label>Port of Loading</label>
                <input
                  type="text"
                  name="portOfLoading"
                  value={bulkData.portOfLoading}
                  onChange={handleBulkDataChange}
                  disabled={loading}
                  list="port-loading-list"
                  placeholder="Type or select port..."
                />
                <datalist id="port-loading-list">
                  {portOptions.map(port => (
                    <option key={port.portId} value={port.portCode}>
                      {port.portName} ({port.portCode})
                    </option>
                  ))}
                </datalist>
                {portErrors.portOfLoading && (
                  <small className="error-text">{portErrors.portOfLoading}</small>
                )}
              </div>
              
              <div className="form-group">
                <label>Port of Discharge</label>
                <input
                  type="text"
                  name="portOfDischarge"
                  value={bulkData.portOfDischarge}
                  onChange={handleBulkDataChange}
                  disabled={loading}
                  list="port-discharge-list"
                  placeholder="Type or select port..."
                />
                <datalist id="port-discharge-list">
                  {portOptions.map(port => (
                    <option key={port.portId} value={port.portCode}>
                      {port.portName} ({port.portCode})
                    </option>
                  ))}
                </datalist>
                {portErrors.portOfDischarge && (
                  <small className="error-text">{portErrors.portOfDischarge}</small>
                )}
              </div>
            </div>

            {/* Shipping Details */}
            <div className="form-row">
              <div className="form-group">
                <label>Vessel Name</label>
                <input
                  type="text"
                  name="vesselName"
                  value={bulkData.vesselName}
                  onChange={handleBulkDataChange}
                  disabled={loading}
                  placeholder="e.g., Ever Given"
                />
              </div>
              
              <div className="form-group">
                <label>Voyage Number</label>
                <input
                  type="text"
                  name="voyageNumber"
                  value={bulkData.voyageNumber}
                  onChange={handleBulkDataChange}
                  disabled={loading}
                  placeholder="e.g., 001W"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>ETD</label>
                <input
                  type="date"
                  name="etd"
                  value={bulkData.etd}
                  onChange={handleBulkDataChange}
                  disabled={loading}
                />
              </div>
              
              <div className="form-group">
                <label>ETA</label>
                <input
                  type="date"
                  name="eta"
                  value={bulkData.eta}
                  onChange={handleBulkDataChange}
                  disabled={loading}
                />
              </div>
            </div>
            <div className="form-row">
                <div className="form-group">
                    <label>Free Days</label>
                    <input
                        type="number"
                        name="freeDays"
                        value={bulkData.freeDays}
                        onChange={handleBulkDataChange}
                        disabled={loading}
                        min="0"
                        placeholder="e.g., 5"
                    />
                </div>
            </div>

            {/* Selection Summary */}
            <div className="selection-summary">
              <strong>{selectedContainers.length}</strong> containers selected
              {selectedContainers.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedContainers([])}
                  className="btn-clear"
                >
                  Clear All
                </button>
              )}
            </div>

            {/* Update Button */}
            <button
              onClick={handleBulkUpdate}
              disabled={loading || selectedContainers.length === 0}
              className="btn btn-primary bulk-update-btn"
            >
              {loading ? <LoadingSpinner size="small" text="" /> : 'Process Selected Containers'}
            </button>
          </div>

          {/* Results Display */}
          {result && (
            <div className="results-card">
              <h4>Operation Results</h4>
              <div className="result-stats">
                <div className="stat success">
                  <span className="count">{result.successCount}</span>
                  <span className="label">Successful</span>
                </div>
                <div className="stat failure">
                  <span className="count">{result.failureCount}</span>
                  <span className="label">Failed</span>
                </div>
              </div>

              {result.failureCount > 0 && (
                <div className="failures-list">
                  <h5>Failed Operations:</h5>
                  {result.failedContainers.map((container, index) => (
                    <div key={container} className="failure-item">
                      <span className="container-number">{container}</span>
                      <span className="reason">{result.failureReasons[index] || 'Unknown error'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Panel - Container Selection */}
        <div className="selection-panel">
          <div className="selection-header">
            <h4>Select Containers ({selectedContainers.length} selected)</h4>
            <textarea
                placeholder="Search containers or bookings... (comma separated)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                rows={4}
                style={{ resize: 'vertical', width: '100%', minHeight: '38px', fontSize: '0.9rem', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
              />
          </div>

          <div className="containers-list">
            <div className="list-header">
              <label className="select-all">
                <input
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={selectedContainers.length === filteredContainers.length && filteredContainers.length > 0}
                />
               <span style={{ marginLeft: '5px' }}>Select All</span>
              </label>
              <span className="count">{filteredContainers.length} containers</span>
            </div>

            <div className="table-container" style={{ maxHeight: '505px', overflowY: 'auto', overflowX: 'auto' }}>
              <table className="table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead className="sticky-header">
                  <tr>
                    <th></th>
                    <th>Container</th>
                    <th>Booking</th>
                    <th>Carrier</th>
                    <th>Ports</th>
                    <th>Vessel</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContainers.map(container => (
                    <tr key={container.id} className={`container-row ${
                      selectedContainers.includes(container.containerNumber) ? 'selected' : ''
                    }`}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedContainers.includes(container.containerNumber)}
                          onChange={(e) => handleSelectContainer(container.containerNumber, e.target.checked)}
                        />
                      </td>
                      <td>
                        <div className="container-number">{container.containerNumber}</div>
                      </td>
                      <td>
                        <div className="booking-number">{container.bookingNumber}</div>
                      </td>
                      <td>
                        <span className="carrier">{container.shippingLine || 'N/A'}</span>
                      </td>
                      <td>
                        <span className="route">
                          {container.portOfLoading || 'N/A'}
                          <br />
                          <span className="arrow">↓</span>
                          <br />
                          {container.portOfDischarge || 'N/A'}
                        </span>
                      </td>
                      <td>
                        <span className="vessel">
                          {container.vesselName || 'N/A'}
                          {container.voyageNumber && (
                            <>
                              <br />
                              <span className="voyage">{container.voyageNumber}</span>
                            </>
                          )}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredContainers.length === 0 && (
              <div className="empty-state">
                <p>No containers found</p>
              </div>
            )}

            {/* Selected Containers Preview */}
            {selectedContainers.length > 0 && (
              <div className="selected-preview">
                <h5>Selected Containers:</h5>
                <div className="selected-tags">
                  {selectedContainers.map(containerNumber => (
                    <span key={containerNumber} className="selected-tag">
                      {containerNumber}
                      <button
                        type="button"
                        onClick={() => handleSelectContainer(containerNumber, false)}
                        className="remove-tag"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .table th {
          position: sticky;
          top: 0;
          background: #f8f9fa;
          z-index: 2;
        }
        .bulk-container-update {
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          padding: 2rem;
        }
        
        .tool-header {
          margin-bottom: 2rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #eee;
        }
        
        .tool-header h3 {
          margin: 0 0 0.5rem 0;
          color: #333;
        }
        
        .tool-header p {
          margin: 0;
          color: #666;
        }
        
        .bulk-layout {
          display: grid;
          grid-template-columns: 400px 1fr;
          gap: 2rem;
          min-height: 600px;
        }
        
        .form-panel {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }
        
        .form-card, .results-card {
          background: #f8f9fa;
          border-radius: 8px;
          padding: 1.5rem;
          border: 1px solid #e9ecef;
        }
        
        .form-card h4, .results-card h4 {
          margin: 0 0 1rem 0;
          color: #333;
        }
        
        .form-row {
          display: flex;
          gap: 1rem;
          margin-bottom: 1rem;
        }
        
        .form-group {
          flex: 1;
          min-width: 0;
        }
        
        .form-group label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 500;
          color: #333;
          font-size: 0.9rem;
        }
        
        .form-group input,
        .form-group select {
          width: 100%;
          padding: 0.5rem;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 0.9rem;
        }
        
        .input-with-button {
          display: flex;
          gap: 0.5rem;
        }
        
        .btn-add {
          padding: 0.5rem 0.75rem;
          background: #6c757d;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.85rem;
          white-space: nowrap;
        }
        
        .btn-add:hover:not(:disabled) {
          background: #5a6268;
        }
        
        .btn-add:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .error-text {
          color: #dc3545;
          font-size: 0.8rem;
          margin-top: 0.25rem;
          display: block;
        }
        
        .checking {
          color: #666;
          font-size: 0.8rem;
          margin-top: 0.25rem;
          display: block;
        }
        
        .selection-summary {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          background: white;
          border-radius: 4px;
          margin: 1rem 0;
          font-size: 1rem;
        }
        
        .btn-clear {
          background: none;
          border: 1px solid #dc3545;
          color: #dc3545;
          padding: 0.25rem 0.75rem;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.85rem;
        }
        
        .btn-clear:hover {
          background: #dc3545;
          color: white;
        }
        
        .bulk-update-btn {
          width: 100%;
          padding: 0.75rem;
          font-size: 1rem;
        }
        
        .result-stats {
          display: flex;
          gap: 1rem;
          margin-bottom: 1rem;
        }
        
        .stat {
          flex: 1;
          text-align: center;
          padding: 0.75rem;
          border-radius: 6px;
          color: white;
        }
        
        .stat.success {
          background: #28a745;
        }
        
        .stat.failure {
          background: #dc3545;
        }
        
        .stat .count {
          display: block;
          font-size: 1.25rem;
          font-weight: bold;
        }
        
        .stat .label {
          font-size: 0.85rem;
        }
        
        .selection-panel {
          display: flex;
          flex-direction: column;
        }
        
        .selection-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }
        
        .search-box input {
          padding: 0.5rem;
          border: 1px solid #ddd;
          border-radius: 4px;
          width: 250px;
          font-size: 0.9rem;
        }
        
        .containers-list {
          flex: 1;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        
        .list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem;
          background: #f8f9fa;
          border-bottom: 1px solid #e9ecef;
          font-size: 0.9rem;
        }
        
        .table-container {
          flex: 1;
          overflow-y: auto;
          overflow-x: auto;
        }
        
        .table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.85rem;
        }
        
        .table th {
          position: sticky;
          top: 0;
          background: #f8f9fa;
          padding: 0.75rem;
          text-align: left;
          font-weight: 600;
          border-bottom: 2px solid #dee2e6;
        }
        
        .table td {
          padding: 0.75rem;
          border-bottom: 1px solid #e9ecef;
        }
        
        .container-row:hover {
          background: #f8f9fa;
        }
        
        .container-row.selected {
          background: #e7f3ff;
        }
        
        .container-number, .booking-number {
          font-weight: 500;
        }
        
        .selected-preview {
          padding: 0.75rem;
          border-top: 1px solid #e9ecef;
          background: #f8f9fa;
        }
        
        .selected-preview h5 {
          margin: 0 0 0.5rem 0;
          font-size: 0.9rem;
        }
        
        .selected-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }
        
        .selected-tag {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          background: #007bff;
          color: white;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.8rem;
        }
        
        .remove-tag {
          background: none;
          border: none;
          color: white;
          cursor: pointer;
          font-size: 1rem;
          line-height: 1;
          padding: 0;
          width: 14px;
          height: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .empty-state {
          text-align: center;
          padding: 3rem;
          color: #666;
          font-size: 0.9rem;
        }
        
        .alert {
          padding: 0.75rem;
          border-radius: 4px;
          margin-bottom: 1rem;
          font-size: 0.9rem;
        }
        
        @media (max-width: 1024px) {
          .bulk-layout {
            grid-template-columns: 1fr;
          }
          
          .form-row {
            flex-direction: column;
            gap: 0.75rem;
          }
        }
      `}</style>
    </div>
  );
};

export default BulkContainerUpdate;